import { ApidaeGridItem } from './apidaeListAPI';
import { isApidaeEventType } from './apidaeEventDates';

export type ApidaeGridSortMode = 'openingDate' | 'city' | 'title' | 'random';

/** Clé de tri pour les fiches sans date (placées en fin de liste). */
const NO_DATE_SORT_KEY = '9999-99-99';

function imageScore(item: ApidaeGridItem): number {
  return item.urlimg ? 1 : 0;
}

function compareByImage(a: ApidaeGridItem, b: ApidaeGridItem): number {
  return imageScore(b) - imageScore(a);
}

function compareByTitle(a: ApidaeGridItem, b: ApidaeGridItem): number {
  return (a.storeName || '').localeCompare(b.storeName || '', 'fr', { sensitivity: 'base' });
}

function compareByCity(a: ApidaeGridItem, b: ApidaeGridItem): number {
  const cityCmp = (a.city || '').localeCompare(b.city || '', 'fr', { sensitivity: 'base' });
  return cityCmp !== 0 ? cityCmp : compareByTitle(a, b);
}

function compareByOpeningDate(a: ApidaeGridItem, b: ApidaeGridItem): number {
  const aDate = a.eventPeriod?.dateDebut ?? NO_DATE_SORT_KEY;
  const bDate = b.eventPeriod?.dateDebut ?? NO_DATE_SORT_KEY;
  if (aDate !== bDate) return aDate.localeCompare(bDate);
  return compareByTitle(a, b);
}

/**
 * Tri par défaut si aucun réglage Sanity : date pour les événements (types agenda),
 * titre pour les autres. Fiches sans date en fin de liste.
 */
function compareAutoDefault(a: ApidaeGridItem, b: ApidaeGridItem): number {
  const aEvent = isApidaeEventType(a.apidaeType);
  const bEvent = isApidaeEventType(b.apidaeType);
  const aHasDate = !!a.eventPeriod?.dateDebut;
  const bHasDate = !!b.eventPeriod?.dateDebut;

  if (aEvent && aHasDate && !(bEvent && bHasDate)) return -1;
  if (bEvent && bHasDate && !(aEvent && aHasDate)) return 1;

  if (aEvent && bEvent && aHasDate && bHasDate) {
    return compareByOpeningDate(a, b);
  }

  return compareByTitle(a, b);
}

/** Date du jour (Europe/Paris) au format YYYY-MM-DD — seed stable sur la journée. */
export function getParisDayKey(date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(date);
}

function hashSeed(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** PRNG déterministe (Mulberry32). */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle<T>(items: T[], seed: string): T[] {
  const arr = [...items];
  const random = mulberry32(hashSeed(seed));
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Mélange déterministe pour la journée (Paris), en gardant les fiches avec image en premier.
 */
function sortRandomWithImagePriority(
  items: ApidaeGridItem[],
  seedSalt?: string | number | null
): ApidaeGridItem[] {
  const day = getParisDayKey();
  const salt = seedSalt != null && seedSalt !== '' ? String(seedSalt) : 'default';
  const withImage = items.filter((it) => imageScore(it) > 0);
  const withoutImage = items.filter((it) => imageScore(it) === 0);
  return [
    ...seededShuffle(withImage, `${day}:${salt}:img`),
    ...seededShuffle(withoutImage, `${day}:${salt}:noimg`),
  ];
}

export function sortApidaeGridItems(
  items: ApidaeGridItem[],
  sortMode?: ApidaeGridSortMode | null,
  seedSalt?: string | number | null
): ApidaeGridItem[] {
  if (sortMode === 'random') {
    return sortRandomWithImagePriority(items, seedSalt);
  }

  return [...items].sort((a, b) => {
    const byImage = compareByImage(a, b);
    if (byImage !== 0) return byImage;

    switch (sortMode) {
      case 'openingDate':
        return compareByOpeningDate(a, b);
      case 'city':
        return compareByCity(a, b);
      case 'title':
        return compareByTitle(a, b);
      default:
        return compareAutoDefault(a, b);
    }
  });
}
